/*
  Slideshow Plus
  --------------
  Deux custom elements, volontairement indépendants de global.js :

  <slideshow-plus>        : empêche la navigation quand un clic est en fait un swipe,
                            et applique une durée d'autoplay propre à chaque slide
                            (attribut data-duration, en secondes).
  <slideshow-plus-video>  : choisit la source vidéo selon le breakpoint,
                            ne charge qu'un seul fichier, et met en pause
                            les vidéos des slides non visibles.

  La logique de carrousel (autoplay, compteur, flèches) reste celle de
  SlideshowComponent dans global.js : elle n'est pas dupliquée. On se contente
  d'ajuster autoplaySpeed avant chaque play() et de relancer le timer à chaque
  changement de slide.
*/

const SP_DESKTOP_QUERY = '(min-width: 750px)';
const SP_DRAG_THRESHOLD = 10; // px

class SlideshowPlus extends HTMLElement {
  connectedCallback() {
    this.slider = this.querySelector('.slider');
    if (!this.slider) return;

    this.pointerStartX = 0;
    this.scrollStart = 0;

    this.onPointerDown = this.handlePointerDown.bind(this);
    this.onClickCapture = this.handleClick.bind(this);

    this.addEventListener('pointerdown', this.onPointerDown, true);
    this.addEventListener('click', this.onClickCapture, true);

    // Dans l'éditeur de thème, <slideshow-component> peut être mis à niveau
    // juste après ce connectedCallback : on attend une micro-tâche.
    customElements.whenDefined('slideshow-component').then(() => this.setupSlideDurations());
  }

  disconnectedCallback() {
    this.removeEventListener('pointerdown', this.onPointerDown, true);
    this.removeEventListener('click', this.onClickCapture, true);
    if (this.component && this.onSlideChanged) {
      this.component.removeEventListener('slideChanged', this.onSlideChanged);
    }
  }

  setupSlideDurations() {
    const component = this.querySelector('slideshow-component');
    if (!component || typeof component.play !== 'function') return;
    if (component.dataset.spDurations === 'true') return;
    if (!this.slider.querySelector('.slideshow__slide[data-duration]')) return;

    component.dataset.spDurations = 'true';
    this.component = component;

    const nativePlay = component.play;
    const plus = this;
    component.play = function () {
      this.autoplaySpeed = plus.durationFor(this.currentPage);
      return nativePlay.apply(this, arguments);
    };

    // Relance le timer avec la durée de la nouvelle slide (autoplay, flèches, swipe).
    this.onSlideChanged = () => {
      if (this.isAutoplayRunning) component.play();
    };
    component.addEventListener('slideChanged', this.onSlideChanged);

    // Le premier play() a été fait par global.js avec la durée globale.
    if (this.isAutoplayRunning) component.play();
  }

  get isAutoplayRunning() {
    // SlideshowComponent passe aria-live à "off" dans play() et "polite" dans pause().
    return this.slider.dataset.autoplay === 'true' && this.slider.getAttribute('aria-live') === 'off';
  }

  durationFor(page) {
    const slides = this.slider.querySelectorAll('.slideshow__slide');
    const slide = slides[(page || 1) - 1];
    const seconds = parseFloat(slide && slide.dataset.duration) || parseFloat(this.slider.dataset.speed) || 5;
    return seconds * 1000;
  }

  handlePointerDown(event) {
    this.pointerStartX = event.clientX;
    this.scrollStart = this.slider.scrollLeft;
  }

  handleClick(event) {
    // Clic déclenché au clavier (Entrée / Espace) : jamais un swipe.
    if (event.detail === 0) return;

    const movedPointer = Math.abs(event.clientX - this.pointerStartX);
    const movedScroll = Math.abs(this.slider.scrollLeft - this.scrollStart);

    if (movedPointer > SP_DRAG_THRESHOLD || movedScroll > 5) {
      event.preventDefault();
      event.stopPropagation();
    }
  }
}

if (!customElements.get('slideshow-plus')) {
  customElements.define('slideshow-plus', SlideshowPlus);
}

class SlideshowPlusVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;

    this.video.muted = true;
    this.appliedSrc = null;
    this.isVisible = false;

    this.desktopQuery = window.matchMedia(SP_DESKTOP_QUERY);
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.onBreakpointChange = this.applySource.bind(this);
    this.onMotionChange = this.applyPlayState.bind(this);

    this.addMediaListener(this.desktopQuery, this.onBreakpointChange);
    this.addMediaListener(this.motionQuery, this.onMotionChange);

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.isVisible = entry.isIntersecting;
          this.applyPlayState();
        });
      },
      { threshold: 0.35 }
    );
    this.observer.observe(this);

    this.applySource();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    this.removeMediaListener(this.desktopQuery, this.onBreakpointChange);
    this.removeMediaListener(this.motionQuery, this.onMotionChange);
  }

  addMediaListener(query, handler) {
    if (!query) return;
    if (query.addEventListener) query.addEventListener('change', handler);
    else query.addListener(handler); // Safari < 14
  }

  removeMediaListener(query, handler) {
    if (!query) return;
    if (query.removeEventListener) query.removeEventListener('change', handler);
    else query.removeListener(handler);
  }

  get isDesktop() {
    return this.desktopQuery.matches;
  }

  get currentSource() {
    const { videoDesktop, videoMobile } = this.dataset;
    return this.isDesktop ? videoDesktop || videoMobile : videoMobile || videoDesktop;
  }

  get currentPoster() {
    const { posterDesktop, posterMobile } = this.dataset;
    return this.isDesktop ? posterDesktop || posterMobile : posterMobile || posterDesktop;
  }

  get autoplayEnabled() {
    return this.dataset.autoplay !== 'false' && !this.motionQuery.matches;
  }

  applySource() {
    const src = this.currentSource;
    const poster = this.currentPoster;

    if (poster && this.video.getAttribute('poster') !== poster) {
      this.video.setAttribute('poster', poster);
    }

    if (!src || this.appliedSrc === src) return;

    this.appliedSrc = src;
    this.video.src = src;
    this.video.load();
    this.applyPlayState();
  }

  applyPlayState() {
    if (!this.appliedSrc) return;

    if (this.isVisible && this.autoplayEnabled) {
      const playback = this.video.play();
      if (playback && typeof playback.catch === 'function') {
        // Autoplay refusé (mode économie d'énergie, etc.) : on garde le poster.
        playback.catch(() => {});
      }
    } else if (!this.video.paused) {
      this.video.pause();
    }
  }
}

if (!customElements.get('slideshow-plus-video')) {
  customElements.define('slideshow-plus-video', SlideshowPlusVideo);
}
